import { useCallback, useState, useRef, useEffect } from 'react';
import { useAppMessage, useDaily, useLocalParticipant } from '@daily-co/daily-react';

import './Chat.css';
import { ReactComponent as SendChatSVG } from '../Images/buttons/sendChatButton.svg';
import { useTranslation } from 'react-i18next';
import DataModel from '../Util/DataModel';
import { isMobile } from 'react-device-detect';
import KUTE from 'kute.js';

export default function Chat({ showChat, toggleChat, isRecorded = false, meetingSettionIDState }) {
  const callObject = useDaily();
  const localParticipant = useLocalParticipant();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const { t } = useTranslation();

  //メッセージ受信をリッスン
  const sendAppMessage = useAppMessage({
    onAppMessage: useCallback((ev) => {
      if (ev.data.type == 'chat') {
        setMessages((messages) => [
          ...messages,
          {
            msg: ev.data.msg,
            name: ev.data.name,
          },
        ]);

        //下までスクロール
        scrollToBottom();
      }
    }, []),
  });

  useEffect(() => {
    //最初に古いデータの読み込み
    getChatData(meetingSettionIDState);
  }, [meetingSettionIDState]);

  //読み込み時初回処理
  useEffect(() => {
    //下までスクロール
    scrollToBottom();
  }, [showChat]);

  useEffect(() => {
    if (isMobile && showChat) {
      let kute = KUTE.to(
        '.chat',
        { top: 'calc(env(safe-area-inset-top) + 0px)' },
        {
          duration: 300,
          easing: 'easingCubicOut',
          onComplete: () => {},
        },
      );
      kute.start();
    }
  }, [showChat]);

  function toggleFunc() {
    if (isMobile) {
      let kute = KUTE.to(
        '.chat',
        { top: window.innerHeight },
        {
          duration: 300,
          easing: 'easingCubicOut',
          onComplete: () => {
            toggleChat();
          },
        },
      );
      kute.start();
    } else {
      toggleChat();
    }
  }

  //既存チャットの取得
  async function getChatData(meetingSettionID) {
    if (callObject == null && meetingSettionID == null) return;

    let sessionID;
    try {
      if (meetingSettionID != null) {
        sessionID = meetingSettionID;
      } else {
        let session = await callObject.getMeetingSession();
        sessionID = session.meetingSession.id;
      }

      let chatData = await DataModel.getChat(sessionID);

      if (chatData != null) {
        if (chatData.length > 0) {
          var chatAray = [];
          chatData.forEach((chat) => {
            chatAray.push({
              type: 'chat',
              msg: URLReplacer(chat.body),
              name: chat.displayName,
            });
          });

          setMessages(chatAray);

          //下までスクロール
          scrollToBottom();
        }
      }
    } catch (e) {}
  }

  //DBに保存
  async function sendChat(displayName, body) {
    if (callObject == null) return;

    let session;
    try {
      session = await callObject.getMeetingSession();
      await DataModel.sendChat(displayName, body, session.meetingSession.id);
    } catch (e) {
      console.log(e);
    }
  }

  const sendMessage = useCallback(
    (message) => {
      /* Send the message to all participants in the chat - this does not include ourselves!
       * See https://docs.daily.co/reference/daily-js/events/participant-events#app-message
       */

      var oldMessage = message;

      var newMessage = URLReplacer(message);

      //WebSocketでメッセージを送信
      sendAppMessage(
        {
          type: 'chat',
          msg: newMessage,
          name: localParticipant?.user_name || t('chat.Guest'),
        },
        '*',
      );

      //メッセージは自分以外に送られるので、自分用にメッセージをセット
      setMessages([
        ...messages,
        {
          type: 'chat',
          msg: newMessage,
          name: localParticipant?.user_name || t('chat.Guest'),
        },
      ]);

      //DBに保存
      sendChat(localParticipant?.user_name || t('chat.Guest'), oldMessage);

      //下までスクロール
      scrollToBottom();
    },
    [localParticipant, messages, sendAppMessage],
  );

  function URLReplacer(str) {
    let match = str.match(
      /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi,
    );
    let final = str;

    if (match != null) {
      match.map((url) => {
        console.log(url);
        final = final.replace(url, '<a href="' + url + '" target="_BLANK">' + url + '</a>');
      });
    }

    return final;
  }

  const onChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue) return; // don't allow people to submit empty strings
    sendMessage(inputValue);
    setInputValue('');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return showChat ? (
    <aside className="chat">
      <div id="chatHead">
        <div id="chatTitle">{t('chat.Message')}</div>
        <button onClick={toggleFunc} id="chatCloseButton">
          ×
        </button>
      </div>
      <ul className="chat-messages">
        {messages?.map((message, index) => (
          <li key={`message-${index}`} className="chat-message">
            <span className="chat-message-author">{message?.name}</span>:{' '}
            <p className="chat-message-body" dangerouslySetInnerHTML={{ __html: message?.msg }} />
            {/* <p className="chat-message-body">{message?.msg}</p> */}
          </li>
        ))}
        <div ref={messagesEndRef} />
      </ul>
      {isRecorded == false && (
        <div className="add-message">
          <form className="chat-form" onSubmit={handleSubmit}>
            <input
              className="chat-input"
              type="text"
              placeholder={t('chat.Type your message here')}
              value={inputValue}
              onChange={(e) => onChange(e)}
            />
            <button type="submit" className="chat-submit-button">
              <SendChatSVG />
            </button>
          </form>
        </div>
      )}
    </aside>
  ) : null;
}
